import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Banknote, ShieldCheck, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";

const BrokerApplicationPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company_name: "",
    notes: "",
  });

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error("Name, email and phone are required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/broker/public-apply`, {
        name: formData.name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        company_name: formData.company_name.trim() || null,
        notes: formData.notes.trim() || null,
      });
      setSubmitted(true);
      toast.success("Application submitted!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit application");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <section className="pt-32 pb-20 px-4">
          <div className="max-w-xl mx-auto text-center" data-testid="broker-apply-success">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="bg-card border border-border rounded-sm p-10"
            >
              <CheckCircle2 className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h1 className="text-2xl font-serif mb-3">Application Received</h1>
              <p className="text-muted-foreground mb-6">
                Thank you for applying to become a Broker. Our team will review your
                application and contact you at <strong>{formData.email}</strong> shortly.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button onClick={() => navigate("/")} data-testid="broker-apply-home-btn">
                  Back to Home
                </Button>
                <Link to="/agents">
                  <Button variant="outline">Meet Our Agents</Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 pb-12 bg-primary text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/" className="inline-flex items-center text-sm text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Link>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-gold font-medium tracking-widest uppercase text-sm mb-2">
              Partner Program
            </p>
            <h1 className="text-3xl md:text-5xl font-serif mb-4 text-white">Become a Broker</h1>
            <p className="text-white/85 max-w-2xl">
              Brokers help our tenants secure deposits in a transparent, two-party escrow.
              You deposit the agreed amount with us, we hold it safely, and on transaction
              success we release 90% back to you — keeping a 10% service fee.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Form */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto grid md:grid-cols-3 gap-8">
          {/* Side info */}
          <div className="space-y-4 md:col-span-1">
            <div className="bg-card border border-border rounded-sm p-5">
              <Banknote className="w-7 h-7 text-gold mb-2" />
              <h3 className="font-serif text-lg mb-1">How it works</h3>
              <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside">
                <li>Apply via this form</li>
                <li>We review &amp; approve</li>
                <li>Sign in with Google — you’re granted Broker access</li>
                <li>Start depositing &amp; tracking transactions</li>
              </ol>
            </div>
            <div className="bg-card border border-border rounded-sm p-5">
              <ShieldCheck className="w-7 h-7 text-green-600 mb-2" />
              <h3 className="font-serif text-lg mb-1">Safe &amp; transparent</h3>
              <p className="text-sm text-muted-foreground">
                Every deposit is auto-posted to our accounting system. Tenants confirm
                outcomes via a secure link, and accounts pays out automatically.
              </p>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="md:col-span-2 bg-card border border-border rounded-sm p-6 space-y-5"
            data-testid="broker-application-form"
          >
            <div>
              <label className="block text-sm font-medium mb-1.5">Full Name *</label>
              <Input
                required
                value={formData.name}
                onChange={handleChange("name")}
                placeholder="Jane Doe"
                data-testid="broker-apply-name"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Email *</label>
                <Input
                  required
                  type="email"
                  value={formData.email}
                  onChange={handleChange("email")}
                  placeholder="jane@example.com"
                  data-testid="broker-apply-email"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Phone *</label>
                <Input
                  required
                  value={formData.phone}
                  onChange={handleChange("phone")}
                  placeholder="+260 ..."
                  data-testid="broker-apply-phone"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Company / Organisation</label>
              <Input
                value={formData.company_name}
                onChange={handleChange("company_name")}
                placeholder="Optional — if applying on behalf of a company"
                data-testid="broker-apply-company"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Notes / Why Broker?</label>
              <Textarea
                rows={4}
                value={formData.notes}
                onChange={handleChange("notes")}
                placeholder="Tell us briefly about your background or how you intend to use the Broker platform."
                data-testid="broker-apply-notes"
              />
            </div>

            <p className="text-xs text-muted-foreground">
              KYC documents will be requested offline by our team after initial review.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="submit"
                disabled={submitting}
                className="sm:flex-1"
                data-testid="broker-apply-submit-btn"
              >
                {submitting ? "Submitting…" : "Submit Application"}
              </Button>
              <Link to="/">
                <Button type="button" variant="outline" className="w-full sm:w-auto">
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default BrokerApplicationPage;
